import {Cached} from '@utils';
import {CycleListType} from './cycle-list.type';
import {ICycle} from '../../../../../entities';
import {undefined} from 'zod';

/** ------------------------------------------  Type: Cycle List Cycle Actions ------------------------------------------ */
export const CycleListCycleActionType = {
	Preview: 'Preview',
	Edit: 'Edit',
	Download: 'Download',
	Sign: 'Sign',
	SignAll: 'SignAll',
	Replace: 'Replace',
	QuickSend: 'QuickSend',
	Notify: 'Notify',
	SavePrivateCopy: 'SavePrivateCopy',
	SavePublicCopy: 'SavePublicCopy',
	Archive: 'Archive',
	Cancel: 'Cancel',
	Delete: 'Delete',
	Resume: 'Resume',
	SaveAsTemplate: 'SaveAsTemplate',
	Create: 'Create',
	Rename: 'Rename',
	MoveToList: 'MoveToList',
	SaveCopy: 'SaveCopy',
} as const;
export type CycleListCycleActionType = (typeof CycleListCycleActionType)[keyof typeof CycleListCycleActionType];

export type ICycleRowActionData = {
	type: CycleListCycleActionType;
	label: string;
	icon: string;
	condition?: (cycle: ICycle) => boolean;
};

export class CycleListCycleActionsData {
	@Cached()
	static getCycleListAvailableTableRowActions(): Record<CycleListType, CycleListCycleActionType[]> {
		return {
			All: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download],
			InProgress: [
				CycleListCycleActionType.Preview,
				CycleListCycleActionType.Notify,
				CycleListCycleActionType.SavePrivateCopy,
				CycleListCycleActionType.SavePublicCopy,
				CycleListCycleActionType.Download,
				CycleListCycleActionType.Cancel,
			],
			Waiting: [CycleListCycleActionType.Preview, CycleListCycleActionType.Sign, CycleListCycleActionType.Download],
			Future: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download],
			Finished: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download, CycleListCycleActionType.Resume, CycleListCycleActionType.Archive],
			Completing: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download, CycleListCycleActionType.Archive],
			Signed: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download, CycleListCycleActionType.Archive],
			Rejected: [CycleListCycleActionType.Preview, CycleListCycleActionType.Download, CycleListCycleActionType.Resume, CycleListCycleActionType.Cancel],
			PrivateTemplate: [
				CycleListCycleActionType.Edit,
				CycleListCycleActionType.Rename,
				CycleListCycleActionType.MoveToList,
				CycleListCycleActionType.SaveCopy,
				CycleListCycleActionType.Delete,
				CycleListCycleActionType.QuickSend,
			],
			PublicTemplate: [CycleListCycleActionType.Edit, CycleListCycleActionType.MoveToList, CycleListCycleActionType.Delete],
			Draft: [CycleListCycleActionType.Edit, CycleListCycleActionType.SaveAsTemplate, CycleListCycleActionType.Create, CycleListCycleActionType.Archive, CycleListCycleActionType.Delete],
			PrivateForm: [
				CycleListCycleActionType.Preview,
				CycleListCycleActionType.Edit,
				CycleListCycleActionType.Rename,
				CycleListCycleActionType.MoveToList,
				CycleListCycleActionType.SaveCopy,
				CycleListCycleActionType.Replace,
				CycleListCycleActionType.Delete,
			],
			PublicForm: [CycleListCycleActionType.Edit, CycleListCycleActionType.MoveToList, CycleListCycleActionType.Delete],
			SentForm: [],
			SignedApproved: [],
			SignedAlert: [],
			SignedRejected: [],
			SignedTimeout: [],
			Canceled: [],
			Archive: [],
		};
	}

	@Cached()
	static getCycleRowActionData(): Record<CycleListCycleActionType, ICycleRowActionData> {
		return {
			Archive: {
				type: CycleListCycleActionType.Archive,
				label: 'pages.cycles.actions.archive',
				icon: 'fas fa-archive',
				condition: (cycle: ICycle) => true,
			},
			Cancel: {
				type: CycleListCycleActionType.Cancel,
				label: 'pages.cycles.actions.cancel',
				icon: 'fas fa-ban',
				condition: (cycle: ICycle) => true,
			},
			Create: {
				type: CycleListCycleActionType.Create,
				label: 'pages.cycles.actions.create',
				icon: 'fas fa-plus-circle',
				condition: (cycle: ICycle) => true,
			},
			Delete: {
				type: CycleListCycleActionType.Delete,
				label: 'pages.cycles.actions.delete',
				icon: 'fas fa-trash-alt',
				condition: (cycle: ICycle) => true,
			},
			Download: {
				type: CycleListCycleActionType.Download,
				label: 'pages.cycles.actions.download',
				icon: 'fas fa-download',
				condition: (cycle: ICycle) => true,
			},
			Edit: {
				type: CycleListCycleActionType.Edit,
				label: 'pages.cycles.actions.edit',
				icon: 'fas fa-edit',
				condition: (cycle: ICycle) => true,
			},
			MoveToList: {
				type: CycleListCycleActionType.MoveToList,
				label: 'pages.cycles.actions.moveToList',
				icon: 'fas fa-exchange-alt',
				condition: (cycle: ICycle) => true,
			},
			Notify: {
				type: CycleListCycleActionType.Notify,
				label: 'pages.cycles.actions.notify',
				icon: 'fas fa-bell',
				condition: (cycle: ICycle) => true,
			},
			Preview: {
				type: CycleListCycleActionType.Preview,
				label: 'pages.cycles.actions.preview',
				icon: 'fas fa-eye',
				condition: (cycle: ICycle) => true,
			},
			QuickSend: {
				type: CycleListCycleActionType.QuickSend,
				label: 'pages.cycles.actions.quickSend',
				icon: 'fas fa-paper-plane',
				condition: (cycle: ICycle) => true,
			},
			Rename: {
				type: CycleListCycleActionType.Rename,
				label: 'pages.cycles.actions.rename',
				icon: 'fas fa-pen',
				condition: (cycle: ICycle) => true,
			},
			Replace: {
				type: CycleListCycleActionType.Replace,
				label: 'pages.cycles.actions.replace',
				icon: 'fas fa-exchange',
				condition: (cycle: ICycle) => true,
			},
			Resume: {
				type: CycleListCycleActionType.Resume,
				label: 'pages.cycles.actions.resume',
				icon: 'fas fa-play-circle',
				condition: (cycle: ICycle) => true,
			},
			SaveAsTemplate: {
				type: CycleListCycleActionType.SaveAsTemplate,
				label: 'pages.cycles.actions.saveAsTemplate',
				icon: 'fas fa-save',
				condition: (cycle: ICycle) => true,
			},
			SaveCopy: {
				type: CycleListCycleActionType.SaveCopy,
				label: 'pages.cycles.actions.savecopy',
				icon: 'fas fa-copy',
				condition: (cycle: ICycle) => true,
			},
			SavePrivateCopy: {
				type: CycleListCycleActionType.SavePrivateCopy,
				label: 'pages.cycles.actions.savecopy',
				icon: 'fas fa-lock',
				condition: (cycle: ICycle) => true,
			},
			SavePublicCopy: {
				type: CycleListCycleActionType.SavePublicCopy,
				label: 'pages.cycles.actions.savecopy-form',
				icon: 'fas fa-globe',
				condition: (cycle: ICycle) => true,
			},
			Sign: {
				type: CycleListCycleActionType.Sign,
				label: 'pages.cycles.actions.sign',
				icon: 'fas fa-signature',
				condition: (cycle: ICycle) => true,
			},
			SignAll: {
				type: CycleListCycleActionType.SignAll,
				label: 'pages.cycles.actions.signAll',
				icon: 'fas fa-signatures',
				condition: (cycle: ICycle) => true,
			},
		};
	}
}
